/**
 * CreatePieceModal — shared modal for creating a new piece.
 *
 * S-085 / FEAT-006. Extracted from KanbanView so CalendarView can
 * reuse the same create flow (same fields, same POST /pieces call,
 * same TanStack Query invalidation contract). The modal is purely
 * presentational + data-fetching for the publications dropdown; the
 * caller owns the agencyclient scope (active workspace) and decides
 * what to do on success (navigate, invalidate, etc).
 *
 * Contract:
 *   - agencyclientId: workspace scope (drives /publications fetch and
 *     the create payload).
 *   - onClose: dismiss without creating.
 *   - onCreated(pieceId): success callback. Caller invalidates the
 *     ["pieces", agencyclientId] query key and routes to the piece
 *     view (typically /piece/:id after the S-086 rename).
 *
 * The form mirrors the legacy "+ New article" modal: title, slug
 * (auto-derived from title unless touched), content_type, publication,
 * optional editorial direction. RBAC is enforced server-side at
 * POST /pieces; we surface 403 as a friendly message here.
 */

import { useEffect, useState } from "react";
import { hasLibraryRefs } from "@/lib/library-refs";
import { describeRunError } from "@/lib/run-errors";
import { ApiError, useApi, useApiQuery } from "@/lib/api";
import { toSlug } from "@/lib/slug";
import { CONTENT_TYPE_DESCRIPTIONS } from "@/lib/format";
import { TONES } from "@/lib/tones";

interface Publication {
  id: number;
  name: string;
  slug: string;
  defaultLanguage?: string;
  defaultTone?: string;
  spanishCountry?: string | null;
}

const LANGUAGES: Array<{ value: string; label: string }> = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "pt", label: "Portuguese" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
];

// FEAT-015 / BUG-002 — Spanish country variants, in the canonical order used by
// PublicationsView (speaker count + commercial relevance). Empty value =
// inherit the publication's spanish_country default.
const SPANISH_COUNTRY_OPTIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: "MX", label: "Mexico" },
  { code: "US", label: "United States (Spanish)" },
  { code: "CO", label: "Colombia" },
  { code: "AR", label: "Argentina" },
  { code: "PE", label: "Peru" },
  { code: "VE", label: "Venezuela" },
  { code: "ES", label: "Spain" },
  { code: "CL", label: "Chile" },
  { code: "GT", label: "Guatemala" },
  { code: "EC", label: "Ecuador" },
  { code: "CU", label: "Cuba" },
  { code: "BO", label: "Bolivia" },
  { code: "DO", label: "Dominican Republic" },
  { code: "HN", label: "Honduras" },
  { code: "PY", label: "Paraguay" },
  { code: "SV", label: "El Salvador" },
  { code: "NI", label: "Nicaragua" },
  { code: "CR", label: "Costa Rica" },
  { code: "PA", label: "Panama" },
  { code: "PR", label: "Puerto Rico" },
  { code: "UY", label: "Uruguay" },
  { code: "neutral", label: "Neutral Spanish" },
];

interface PublicationsResponse {
  data: Publication[];
  meta: { total: number; limit: number; offset: number };
}

export const CREATE_PIECE_CONTENT_TYPES: Array<{
  value: string;
  label: string;
}> = [
  { value: "blog_post", label: "Blog Post" },
  { value: "linkedin_article", label: "LinkedIn Article" },
  { value: "substack", label: "Substack" },
  { value: "technical", label: "Technical" },
  { value: "internal_memo", label: "Internal Memo" },
  { value: "personal", label: "Personal" },
  { value: "news_analysis", label: "News Analysis" },
  { value: "short_story", label: "Short Story" },
  { value: "serial_episode", label: "Serial Episode" },
  { value: "novella_chapter", label: "Novella Chapter" },
  { value: "flash_fiction", label: "Flash Fiction" },
];

export interface CreatePieceModalProps {
  agencyclientId: number;
  onClose: () => void;
  onCreated: (pieceId: number) => void;
}

/**
 * Insert a library doc slug into editorial direction. If a "library docs:" line
 * already exists, append the slug to it (de-duplicated); otherwise add a new
 * line. The orchestrator parses this with parseLibraryRefs server-side.
 */
export function addLibraryRef(current: string, slug: string): string {
  const re = /library docs?:\s*([a-z0-9-]+(?:\s*,\s*[a-z0-9-]+)*)/i;
  const match = re.exec(current);
  if (!match) {
    const prefix = current.trim() ? `${current.trim()}\n` : "";
    return `${prefix}library docs: ${slug}`;
  }
  const slugs = match[1]!.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (slugs.includes(slug)) return current;
  slugs.push(slug);
  return current.replace(re, `library docs: ${slugs.join(", ")}`);
}

/**
 * The four fiction kinds. They run a separate pipeline, so choosing one drops
 * the tone and language controls entirely rather than disabling them — the
 * fiction pipeline has no use for either. The handoff also draws them
 * visually quieter, so the seven kinds most pieces use lead.
 */
export const FICTION_CONTENT_TYPES = new Set([
  "short_story",
  "serial_episode",
  "novella_chapter",
  "flash_fiction",
]);

interface PieceCreateBody {
  agencyclient_id: number;
  publication_id: number;
  title: string;
  content_type: string;
  tone?: string;
  language?: string;
  spanish_country_override?: string | null;
  restrict_sources_to_language?: boolean;
  only_these_sources?: boolean;
  slug?: string;
  editorial_direction?: string;
}

export function CreatePieceModal({
  agencyclientId,
  onClose,
  onCreated,
}: CreatePieceModalProps) {
  const api = useApi();
  const { data: pubResponse, isLoading: pubsLoading } =
    useApiQuery<PublicationsResponse>(
      ["publications", agencyclientId],
      `/publications?agencyclient_id=${agencyclientId}`
    );

  const publications = pubResponse?.data ?? [];

  // FEAT-016 — library document slugs for the "library docs:" autocomplete hint.
  const { data: libraryResp } = useApiQuery<{ data: { slug: string; name: string }[] }>(
    ["library-documents", agencyclientId],
    `/library-documents?agencyclient_id=${agencyclientId}`
  );
  const libraryDocs = libraryResp?.data ?? [];

  const [creationMode, setCreationMode] = useState<"ai" | "manual">("ai");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [contentType, setContentType] = useState("blog_post");
  const [tone, setTone] = useState("professional");
  const [toneTouched, setToneTouched] = useState(false);
  const [language, setLanguage] = useState("en");
  const [languageTouched, setLanguageTouched] = useState(false);
  const [spanishCountry, setSpanishCountry] = useState("");
  const [restrictSources, setRestrictSources] = useState(false);
  // S-382 — "Only these sources": no web research, run on the bound docs.
  const [onlyTheseSources, setOnlyTheseSources] = useState(false);
  const [publicationId, setPublicationId] = useState<number | "">("");
  const [direction, setDirection] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (publicationId === "" && publications.length) {
      setPublicationId(publications[0].id);
    }
  }, [publications, publicationId]);

  // BUG-001 — default the language to the selected publication's language until
  // the user picks one explicitly. The piece value overrides the publication
  // default in the pipeline.
  useEffect(() => {
    if (languageTouched || typeof publicationId !== "number") return;
    const pub = publications.find((p) => p.id === publicationId);
    if (pub?.defaultLanguage) setLanguage(pub.defaultLanguage);
  }, [publicationId, publications, languageTouched]);

  // BUG-028 — mirrors the language default above: pre-fill the tone dropdown
  // from the selected publication's default_tone until the user picks one
  // explicitly. Without this, the field always sent "professional" (its
  // initial state), so the API's publication-default fallback never fired.
  useEffect(() => {
    if (toneTouched || typeof publicationId !== "number") return;
    const pub = publications.find((p) => p.id === publicationId);
    if (pub?.defaultTone) setTone(pub.defaultTone);
  }, [publicationId, publications, toneTouched]);

  useEffect(() => {
    if (!slugTouched) setSlug(toSlug(title));
  }, [title, slugTouched]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const canCreate =
    !!title.trim() &&
    !!slug.trim() &&
    typeof publicationId === "number" &&
    !submitting;

  async function submit() {
    if (!canCreate || typeof publicationId !== "number") return;
    setError(null);
    setSubmitting(true);
    try {
      const body: PieceCreateBody = {
        agencyclient_id: agencyclientId,
        publication_id: publicationId,
        title: title.trim(),
        content_type: contentType,
        tone: FICTION_CONTENT_TYPES.has(contentType) ? undefined : tone,
        language: FICTION_CONTENT_TYPES.has(contentType) ? undefined : language,
        slug: slug.trim(),
      };
      if (
        !FICTION_CONTENT_TYPES.has(contentType) &&
        language === "es" &&
        spanishCountry
      ) {
        body.spanish_country_override = spanishCountry;
      }
      if (
        !FICTION_CONTENT_TYPES.has(contentType) &&
        language !== "en" &&
        restrictSources
      ) {
        body.restrict_sources_to_language = true;
      }
      // Only sent when it can mean something: the checkbox is disabled
      // without a binding, but state can outlive the direction being edited.
      if (onlyTheseSources && hasLibraryRefs(direction)) {
        body.only_these_sources = true;
      }
      if (direction.trim()) body.editorial_direction = direction.trim();
      const created = await api<{ id: number }>("/pieces", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (creationMode === "ai") {
        // FEAT-089 — this was `void api(...).catch(() => {})`: fire-and-forget
        // with the error thrown away. In a manual workspace the run endpoint
        // used to answer 409 run_mode_manual, and the empty catch ate the one
        // sentence that would have explained why nothing happened. The founder
        // created a piece, watched it sit in Ideas, and had no way to tell a
        // refusal from a slow queue. Awaited now, so a real failure reaches the
        // form the user is still looking at rather than the void.
        //
        // The piece itself is already created at this point — a failed run is a
        // piece that did not start, not a piece that does not exist, and the
        // message says so.
        try {
          await api(`/pieces/${created.id}/run`, { method: "POST" });
        } catch (runErr) {
          setError(
            `Created, but could not start the pipeline: ${describeRunError(runErr)}`
          );
          setSubmitting(false);
          return;
        }
      }
      onCreated(created.id);
    } catch (e) {
      const message =
        e instanceof ApiError
          ? e.status === 403
            ? "You don't have permission to create pieces in this workspace."
            : (e.message ?? "Could not create piece.")
          : e instanceof Error
            ? e.message
            : "Could not create piece.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="na-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="na-modal" role="dialog" style={{ maxWidth: 560 }}>
        <header className="na-head">
          <div className="na-head-l">
            <div className="na-eyebrow">New piece</div>
            <h2 className="na-title">Create a piece</h2>
          </div>
          <div className="na-head-r">
            <button className="na-x" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </header>

        <div className="na-body na-body-brief">
          <div className="na-form">
            {error && (
              <div className="login-error" style={{ marginBottom: 12 }}>
                {error}
              </div>
            )}
            <div className="na-mode-row">
              {(["ai", "manual"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`na-mode-card ${creationMode === mode ? "is-active" : ""}`}
                  onClick={() => setCreationMode(mode)}
                >
                  <span className="na-mode-title">
                    {mode === "ai" ? "AI Pipeline" : "Write myself"}
                  </span>
                  <span className="na-mode-desc">
                    {mode === "ai"
                      ? "Scout, write, and polish automatically"
                      : "Start with a blank draft"}
                  </span>
                </button>
              ))}
            </div>
            <div className="na-grid">
              <div className="na-field na-field-full">
                <label className="na-label">
                  Title <span className="na-req">*</span>
                </label>
                <input
                  className="na-input"
                  placeholder="Piece headline"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="na-field na-field-full">
                <label className="na-label">
                  Slug <span className="na-req">*</span>
                </label>
                <input
                  className="na-input mono"
                  placeholder="piece-slug"
                  value={slug}
                  onChange={(e) => {
                    setSlug(toSlug(e.target.value));
                    setSlugTouched(true);
                  }}
                />
                <div className="na-hint">Auto-derived from title</div>
              </div>

              <div className="na-field na-field-full">
                <label className="na-label">Content type</label>
                {/* S-372 — pills, not a dropdown: eleven kinds is a set worth
                    seeing at once, and the fiction four need to read as a
                    different thing rather than as four more rows. */}
                <div className="na-kinds" role="radiogroup" aria-label="Content type">
                  {CREATE_PIECE_CONTENT_TYPES.map((c) => {
                    const fiction = FICTION_CONTENT_TYPES.has(c.value);
                    return (
                      <button
                        key={c.value}
                        type="button"
                        role="radio"
                        aria-checked={contentType === c.value}
                        className={`na-kind ${contentType === c.value ? "is-on" : ""} ${
                          fiction ? "is-fiction" : ""
                        }`}
                        onClick={() => setContentType(c.value)}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
                {CONTENT_TYPE_DESCRIPTIONS[contentType] && (
                  <div className="na-hint">{CONTENT_TYPE_DESCRIPTIONS[contentType]}</div>
                )}
              </div>

              {!FICTION_CONTENT_TYPES.has(contentType) && (
                <div className="na-field">
                  <label className="na-label">Tone</label>
                  <select
                    className="na-select"
                    value={tone}
                    onChange={(e) => {
                      setTone(e.target.value);
                      setToneTouched(true);
                    }}
                  >
                    {TONES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <div className="na-hint">
                    {TONES.find((t) => t.value === tone)?.desc}
                  </div>
                </div>
              )}

              {!FICTION_CONTENT_TYPES.has(contentType) && (
                <div className="na-field">
                  <label className="na-label">Language</label>
                  <select
                    className="na-select"
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                      setLanguageTouched(true);
                    }}
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                  <div className="na-hint">
                    Defaults to the publication's language. Only Spanish has
                    regional tuning; others use a general directive.
                  </div>
                </div>
              )}

              {!FICTION_CONTENT_TYPES.has(contentType) && language !== "en" && (
                <div className="na-field">
                  <label
                    className="na-label"
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={restrictSources}
                      onChange={(e) => setRestrictSources(e.target.checked)}
                    />
                    Only cite sources in {LANGUAGES.find((l) => l.value === language)?.label}
                  </label>
                  <div className="na-hint">
                    Restricts Scout's web search to {LANGUAGES.find((l) => l.value === language)?.label.toLowerCase()}-language
                    results, instead of finding sources in any language.
                  </div>
                </div>
              )}

              {!FICTION_CONTENT_TYPES.has(contentType) && language === "es" && (
                <div className="na-field">
                  <label className="na-label">Spanish country variant</label>
                  <select
                    className="na-select"
                    value={spanishCountry}
                    onChange={(e) => setSpanishCountry(e.target.value)}
                  >
                    <option value="">Inherit from publication</option>
                    {SPANISH_COUNTRY_OPTIONS.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="na-hint">
                    {(() => {
                      const pub =
                        typeof publicationId === "number"
                          ? publications.find((p) => p.id === publicationId)
                          : undefined;
                      const inherited = pub?.spanishCountry
                        ? SPANISH_COUNTRY_OPTIONS.find(
                            (o) => o.code === pub.spanishCountry
                          )?.label ?? pub.spanishCountry
                        : "Neutral Spanish";
                      return `Overrides the publication default (${inherited}) for this piece only.`;
                    })()}
                  </div>
                </div>
              )}

              <div className="na-field na-field-full">
                <label className="na-label">
                  Publication <span className="na-req">*</span>
                </label>
                <select
                  className="na-select"
                  value={publicationId}
                  onChange={(e) =>
                    setPublicationId(
                      e.target.value ? Number(e.target.value) : ""
                    )
                  }
                  disabled={pubsLoading}
                >
                  <option value="" disabled>
                    {pubsLoading
                      ? "Loading…"
                      : publications.length
                        ? "— pick a publication —"
                        : "No publications — create one first"}
                  </option>
                  {publications.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {!pubsLoading && publications.length === 0 && (
                  <div className="na-hint">
                    Go to Settings → Publications to create one.
                  </div>
                )}
              </div>

              <div className="na-field na-field-full">
                <label className="na-label">Editorial direction</label>
                <textarea
                  className="na-textarea"
                  rows={4}
                  placeholder="Optional — angle, focus, sources the Scout should prioritize."
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                />
                {libraryDocs.length > 0 && (
                  <div className="na-hint" style={{ marginTop: 6 }}>
                    <span style={{ marginRight: 6 }}>Library docs:</span>
                    {libraryDocs.map((d) => (
                      <button
                        key={d.slug}
                        type="button"
                        className="na-chip"
                        title={`Reference ${d.name}`}
                        style={{ marginRight: 6, marginBottom: 4 }}
                        onClick={() => setDirection((cur) => addLibraryRef(cur, d.slug))}
                      >
                        + {d.slug}
                      </button>
                    ))}
                  </div>
                )}
                {/* S-382 — the design's "Only these sources". Disabled until
                    the direction actually binds a document: skipping research
                    with nothing bound starves the Writer rather than scoping
                    it, and a control that can produce an unsourced draft is
                    worse than no control. */}
                <label
                  className="na-hint"
                  style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}
                  title={
                    hasLibraryRefs(direction)
                      ? "Skip web research — write from the bound documents only"
                      : "Add a library doc above first"
                  }
                >
                  <input
                    type="checkbox"
                    checked={onlyTheseSources && hasLibraryRefs(direction)}
                    disabled={!hasLibraryRefs(direction)}
                    onChange={(e) => setOnlyTheseSources(e.target.checked)}
                  />
                  <span>
                    Only these sources
                    {!hasLibraryRefs(direction) && " — add a library doc first"}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <footer className="na-foot">
          {/* S-372 — the footer says where the piece starts, so the choice of
              mode has a visible consequence before you commit to it. */}
          <div className="na-foot-l">
            <span className="na-foot-meta">
              {creationMode === "ai"
                ? FICTION_CONTENT_TYPES.has(contentType)
                  ? "Starts at the Worldbuilder"
                  : onlyTheseSources && hasLibraryRefs(direction)
                    ? "Skips the Scout — writes from the bound documents"
                    : "Starts at the Scout"
                : "No pipeline run — you write the draft"}
            </span>
          </div>
          <div className="na-actions">
            <button className="na-btn na-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              className="na-btn na-btn-primary"
              disabled={!canCreate}
              onClick={submit}
            >
              {submitting
                ? "Creating…"
                : creationMode === "ai"
                  ? "Create & run pipeline"
                  : "Create piece"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
